import React, { useEffect, useState } from 'react';
import { api, isSpendApprovalError, permissionDeniedMessage } from './api';
import { toast } from 'sonner';
import { FileSignature, Loader2, X, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Input } from '../components/ui/input';
import ContactSearchInput from './shared/ContactSearchInput';
import { contactFields } from './shared/useTextRecipient';

const NAVY = '#070B5F';
const GREEN = '#6BA82E';

/**
 * Send a PDF for e-signature via DocuSign.
 * Props: worksheetId (optional — use LE PDF), onClose, onSent
 */
export default function DocuSignEnvelopeModal({ worksheetId, dealId, defaultSubject, onClose, onSent }) {
  const [status, setStatus] = useState(null);
  const [subject, setSubject] = useState(defaultSubject || 'Please sign');
  const [message, setMessage] = useState('');
  const [signers, setSigners] = useState([{ name: '', email: '', role_name: 'Signer', embedded: false }]);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get('/docusign/status').then(({ data }) => setStatus(data)).catch(() => {}); }, []);

  const addSigner = () => setSigners((s) => [...s, { name: '', email: '', role_name: 'Signer', embedded: false }]);
  const removeSigner = (i) => setSigners((s) => s.filter((_, j) => j !== i));
  const updateSigner = (i, k, v) => setSigners((s) => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  // Ticket 188: a contact pick fills the row's name + email; both stay editable.
  // A contact with no email blanks it rather than keep the last person's address.
  const pickSigner = (i, c) => {
    const f = contactFields(c);
    setSigners((s) => s.map((x, j) => j === i ? { ...x, name: f.name, email: f.email } : x));
  };

  const send = async () => {
    if (!worksheetId) { toast.error('Only worksheet-source envelopes supported here'); return; }
    if (!signers.every(s => s.name && s.email)) { toast.error('Every signer needs name + email'); return; }
    setSending(true);
    try {
      const payload = {
        subject, message,
        signers: signers.map((s, i) => ({
          ...s,
          client_user_id: s.embedded ? `signer-${i + 1}` : undefined,
        })),
        source: { kind: 'worksheet_pdf', value: worksheetId, file_name: 'loan-estimate.pdf' },
        deal_id: dealId,
        send_now: true,
      };
      const { data } = await api.post('/docusign/envelopes', payload);
      toast.success(`Envelope sent: ${data.envelope.envelope_id.slice(0, 12)}…`);
      onSent && onSent(data.envelope);
    } catch (e) {
      // Tickets 167 + 180: the interceptor already toasted the approval or
      // permission message, and a permission `detail` is an object.
      if (isSpendApprovalError(e) || permissionDeniedMessage(e)) return;
      const detail = e.response?.data?.detail || 'DocuSign send failed';
      toast.error(detail);
      if (detail.includes('consent')) {
        // Extract the consent URL and open it
        const match = detail.match(/https?:\/\/\S+/);
        if (match) window.open(match[0], '_blank');
      }
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}
           data-testid="docusign-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-semibold flex items-center gap-2" style={{ color: NAVY }}>
            <FileSignature size={16} /> Send via DocuSign
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>

        {status && !status.configured && (
          <div className="mb-4 p-3 rounded-lg text-xs" style={{ background: '#fef3c7', color: '#92400e' }} data-testid="docusign-unconfigured">
            DocuSign is not yet configured. Ask your admin to set <code>DOCUSIGN_*</code> env vars.
            See <a className="underline" href="https://developers.docusign.com" target="_blank" rel="noreferrer">developers.docusign.com <ExternalLink size={10} className="inline" /></a>.
          </div>
        )}
        {status && status.configured && (
          <div className="mb-4 text-[11px] text-slate-500" data-testid="docusign-configured">
            Connected to {status.sandbox ? 'Sandbox' : 'Production'} · {status.auth_server}
          </div>
        )}

        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ds-subject"
                   className="mt-1" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Message (optional)</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                      data-testid="ds-message"
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Signers</span>
              <button onClick={addSigner} data-testid="ds-add-signer" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                <Plus size={11} /> Add signer
              </button>
            </div>
            <div className="space-y-2">
              {signers.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center" data-testid={`ds-signer-${i}`}>
                  <ContactSearchInput placeholder="Name (type to find a contact)" value={s.name}
                         onChange={(v) => updateSigner(i, 'name', v)} onPick={(c) => pickSigner(i, c)}
                         testid={`ds-signer-name-${i}`}
                         className="h-9 w-full min-w-0 rounded-md border border-slate-200 px-2 text-sm" />
                  <Input placeholder="Email" type="email" value={s.email} onChange={(e) => updateSigner(i, 'email', e.target.value)}
                         data-testid={`ds-signer-email-${i}`} />
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <input type="checkbox" checked={s.embedded} onChange={(e) => updateSigner(i, 'embedded', e.target.checked)}
                           data-testid={`ds-signer-embedded-${i}`} />
                    Embed
                  </label>
                  {signers.length > 1 && (
                    <button onClick={() => removeSigner(i)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              Embed = sign inside our portal (iframe). Uncheck = DocuSign emails them a link.
            </p>
          </div>
        </div>

        <div className="mt-5 flex justify-between">
          <button onClick={onClose} className="text-sm px-4 h-9 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={send} disabled={sending || !status?.configured}
                  data-testid="ds-send"
                  className="text-sm px-4 h-9 rounded-md text-white font-medium inline-flex items-center gap-2 disabled:opacity-60"
                  style={{ background: GREEN }}>
            {sending ? <Loader2 className="animate-spin" size={13} /> : <FileSignature size={13} />}
            Send for signature
          </button>
        </div>
      </div>
    </div>
  );
}
